import { useState } from "react";
import { Link } from "react-router-dom";
import { Content, Portal, Root, Trigger } from "@radix-ui/react-popover";
import { ChevronDown, MapPin } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { formatBudgetMoney } from "../formatBudgetMoney";
import { getTaxSavingsPromptCopy } from "../TaxSavingsPrompt/TaxSavingsPrompt.utils";
import { isTaxSavingsCurrencySupported } from "../budgetTaxIncentiveNetting";
import type { ProductionBaseChipProps } from "./ProductionBaseChip.types";

const NO_BASE_LABEL = "No production base";

const EMPTY_BUDGET_COPY = {
  message: "Add amounts to this budget to estimate tax savings.",
  linkLabel: null,
};

/**
 * The budget's production base beside the page title. It shows the tax savings status at a
 * glance, and its popover explains it and links to where the base is changed.
 */
export const ProductionBaseChip = ({
  location,
  budgetCurrency,
  currencySymbol,
  budgetTotal,
  taxNetting,
  settingsHref,
  canEdit,
}: ProductionBaseChipProps) => {
  const [open, setOpen] = useState(false);
  const baseLabel = location.productionBaseLabel ?? NO_BASE_LABEL;
  const savings = taxNetting ? formatBudgetMoney(currencySymbol, taxNetting.estimatedRebate) : null;
  const status = savings ? `Saves ${savings}` : "No tax estimate";

  // An empty budget has nothing to estimate yet; that says nothing about the program.
  const isEmptyBudget = budgetTotal <= 0 && isTaxSavingsCurrencySupported(budgetCurrency);
  const promptCopy = taxNetting
    ? null
    : isEmptyBudget
      ? EMPTY_BUDGET_COPY
      : getTaxSavingsPromptCopy({ ...location, budgetCurrency });
  // Saved incentives can win over the base, so name the program the estimate used.
  const message = taxNetting
    ? `Tax savings use the ${taxNetting.primaryJurisdiction} program.`
    : (promptCopy?.message ?? "");
  const linkLabel = taxNetting ? "Change production base" : (promptCopy?.linkLabel ?? null);

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Production base: ${baseLabel} · ${status}`}
          className="inline-flex h-7 max-w-full min-w-0 items-center gap-1.5 rounded-full border border-border bg-muted px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 truncate">{baseLabel}</span>
          <span className="shrink-0 font-normal text-muted-foreground">{` · ${status}`}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </Trigger>
      <Portal>
        <Content
          role="dialog"
          aria-label="Production base"
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-[200] w-[300px] max-w-[calc(100vw-24px)] rounded-[10px] border border-border bg-popover p-3 text-sm text-popover-foreground shadow-[0_12px_32px_rgb(0_0_0/0.2)] outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <p className="font-semibold">Production base</p>
          <p className="mt-1 text-muted-foreground">
            {message}
            {linkLabel && !canEdit ? " Ask the project owner to set the production base." : null}
          </p>
          <dl className="mt-3 text-xs">
            <div className="flex justify-between gap-3 border-t border-border py-1.5">
              <dt className="text-muted-foreground">Base</dt>
              <dd className="truncate text-right font-medium">{baseLabel}</dd>
            </div>
            <div className="flex justify-between gap-3 border-t border-border py-1.5">
              <dt className="text-muted-foreground">Tax savings</dt>
              <dd className="text-right font-medium">{savings ?? "Not estimated"}</dd>
            </div>
          </dl>
          {linkLabel && canEdit && settingsHref ? (
            <Button asChild variant="badge" color="gray" size="sm" className="mt-2">
              <Link to={settingsHref} onClick={() => setOpen(false)}>
                {linkLabel}
              </Link>
            </Button>
          ) : null}
        </Content>
      </Portal>
    </Root>
  );
};
